import { cloneDeep, isEmpty, unset } from 'lodash';

import { FormForm } from '../../../../../components/Form/FormForm';
import { FormPercentageField } from '../../../../../components/Form/TextField/FormPercentageField';
import { Grid } from '../../../../../components/Grid/Grid';
import { Icon } from '../../../../../components/Icon/Icon';
import React from 'react';
import { graphql } from '../../../../../generated';
import { handleError } from '../../../../../model/notification';
import { t } from '../../../../../locales/translate';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { useMutationHandler } from '../../../../../model/graphql';

interface Props {
  countries: Record<string, { name: string; amount: number; linked?: boolean; originalTax?: number }>;
  onClose: () => void;
  productId: string;
  newProduct: boolean;
  onAfterSubmit: () => void;
}

const UPDATE_PRODUCT_TAXES = graphql(`
  mutation UpdateProductTaxes($id: ID!, $taxes: [TaxInput!]!) {
    updateProductTaxes(input: { productId: $id, taxes: $taxes }) {
      product {
        id
      }
    }
  }
`);

export function EditVatForm({ countries, onClose, productId, newProduct, onAfterSubmit }: Props): JSX.Element {
  const [updateProductTaxes, { loading }] = useMutationHandler(UPDATE_PRODUCT_TAXES);
  const formFields = cloneDeep(countries);
  Object.keys(formFields).forEach((countryCode) => unset(formFields[countryCode], 'name'));
  const formValidations = Object.keys(countries).reduce(
    (acc, current) => {
      return {
        ...acc,
        [current]: {
          amount: 'required',
        },
      };
    },
    {} as Record<string, { amount: string }>
  );

  const { fields, dirty, onSubmit, valid, model } = useForm({
    model: formFields,
    // validations type here is the the same as typeof formFields which is not correct
    // hence expect-error
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    validations: formValidations,
    onSubmit: async (formData) => {
      const submitData = newProduct ? formData.model : formData.changes;
      const taxes = Object.entries(submitData).map(([code, value]) => ({
        countryCode: code === 'world' ? null : code,
        amount: value?.linked ? null : (value?.amount ?? null),
      }));

      await updateProductTaxes({ variables: { id: productId, taxes } });
      onAfterSubmit();
      onClose();
    },
    onSubmitError: (error) => handleError(error),
  });

  const getPlaceholder = (
    initialField: { linked?: boolean; amount: number; originalTax?: number },
    isFocused: boolean
  ): string | undefined => {
    if (isFocused) {
      return undefined;
    }

    return initialField.linked ? String(initialField.amount * 100) : String((initialField?.originalTax || 0) * 100);
  };

  return (
    <FormForm onSubmit={onSubmit} canSubmit={valid && (dirty || newProduct)} onClose={onClose} loading={loading}>
      {isEmpty(countries) ? (
        <p>{t.general.noDataToShow}</p>
      ) : (
        <Grid>
          {Object.entries(countries).map(([countryCode, countryData]) => (
            <FormPercentageField
              {...fields[countryCode].fields.amount}
              name={`${countryCode}.amount`}
              onBlur={() => {
                fields[countryCode].fields.amount.onBlur();

                if (model[countryCode].amount === null) {
                  fields[countryCode].fields.amount.onChange(countryData.amount);
                }

                if (model[countryCode].amount === countryData.originalTax) {
                  fields[countryCode].fields.linked.onChange(true);
                }
              }}
              placeholder={getPlaceholder(countryData, fields[countryCode].fields.amount.focused)}
              value={!model[countryCode].linked ? model[countryCode].amount : undefined}
              description={countryCode === 'world' ? t.model.price.nonEU : undefined}
              key={countryCode}
              label={countryCode === 'world' ? t.model.price.world : countryData.name}
              onChange={(v) => {
                fields[countryCode].fields.linked.onChange(false);
                fields[countryCode].fields.amount.onChange(v as number);
              }}
              actions={
                countryCode === 'world' ? null : (
                  <>
                    <Icon
                      name="link"
                      key={String(fields[countryCode].fields.linked.value)}
                      tooltip={fields[countryCode].fields.linked.value ? t.general.unlink : t.general.link}
                      color={fields[countryCode].fields.linked.value ? '#EA3C23' : '#909090'}
                      onClick={() => {
                        fields[countryCode].fields.linked.onChange(!fields[countryCode].fields.linked.value);
                      }}
                    />
                    <input
                      type="hidden"
                      name={`${countryCode}.linked`}
                      value={String(fields[countryCode].fields.linked.value)}
                    />
                  </>
                )
              }
            />
          ))}
        </Grid>
      )}
    </FormForm>
  );
}
